import {canonicalizeModuleCode, ModuleCode} from '../../../platform/modules/moduleRegistry'
import {getRuntimeModuleCode} from '../../../platform/modules/moduleSession'
import {AuthModel} from './_models'
import {refreshModuleToken, refreshToken} from './_requests'

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'
const AUTH_ROLES_LOCAL_STORAGE_KEY = 'kt-auth-roles-react-v'
export const MODULE_AUTH_SESSION_STORAGE_PREFIX = 'module-auth:'
const LEGACY_MODULE_PREVIEW_ENABLED =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_ALLOW_LEGACY_MODULE_PREVIEW === 'true'

// Routes that should remain accessible without authentication.
const PUBLIC_ROUTES = ['/program']

export interface AuthRolesModel {
  roles: AuthModel[]
  activeRoleIndex: number
}

export interface ModuleAuthRolesModel extends AuthRolesModel {
  moduleCode: ModuleCode
  activeUserRoleId?: number
  defaultRoute?: string
}

function getLocalStorage(): Storage | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined
  } catch (error) {
    console.error('AUTH LOCAL STORAGE ACCESS ERROR', error)
    return undefined
  }
}

function getSessionStorage(): Storage | undefined {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : undefined
  } catch (error) {
    console.error('AUTH SESSION STORAGE ACCESS ERROR', error)
    return undefined
  }
}

function readJson<T>(storage: Storage | undefined, key: string, label: string): T | undefined {
  const value = storage?.getItem(key)
  if (!value) {
    return undefined
  }

  try {
    return JSON.parse(value) as T
  } catch (error) {
    console.error(`${label} PARSE ERROR`, error)
    return undefined
  }
}

function writeJson(storage: Storage | undefined, key: string, value: unknown, label: string) {
  try {
    storage?.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.error(`${label} SAVE ERROR`, error)
  }
}

export function getModuleAuthStorageKey(moduleCode: string): string | undefined {
  const code = canonicalizeModuleCode(moduleCode)
  return code ? `${MODULE_AUTH_SESSION_STORAGE_PREFIX}${code}` : undefined
}

export const getLegacyAuthRoles = (): AuthRolesModel | undefined =>
  readJson<AuthRolesModel>(
    getLocalStorage(),
    AUTH_ROLES_LOCAL_STORAGE_KEY,
    'AUTH ROLES LOCAL STORAGE'
  )

export const setLegacyAuthRoles = (authRoles: AuthRolesModel) => {
  writeJson(
    getLocalStorage(),
    AUTH_ROLES_LOCAL_STORAGE_KEY,
    authRoles,
    'AUTH ROLES LOCAL STORAGE'
  )
}

export const getModuleAuthRoles = (
  moduleCode: string
): ModuleAuthRolesModel | undefined => {
  const code = canonicalizeModuleCode(moduleCode)
  const key = code && getModuleAuthStorageKey(code)
  if (!code || !key) {
    return undefined
  }

  const stored = readJson<ModuleAuthRolesModel>(
    getSessionStorage(),
    key,
    'MODULE AUTH SESSION STORAGE'
  )
  if (!stored || canonicalizeModuleCode(stored.moduleCode) !== code) {
    return undefined
  }

  const roles = stored.roles.map((role, index) => ({
    ...role,
    moduleCode: code,
    userRoleId:
      typeof role.userRoleId === 'number'
        ? role.userRoleId
        : LEGACY_MODULE_PREVIEW_ENABLED
        ? -(index + 1)
        : undefined,
  }))
  const activeRoleIndex = Math.max(
    0,
    Math.min(stored.activeRoleIndex || 0, Math.max(roles.length - 1, 0))
  )
  const activeRole = stored.activeUserRoleId
    ? roles.find((role) => role.userRoleId === stored.activeUserRoleId)
    : roles[activeRoleIndex]

  return {
    ...stored,
    moduleCode: code,
    roles,
    activeRoleIndex: activeRole ? roles.indexOf(activeRole) : activeRoleIndex,
    activeUserRoleId: activeRole?.userRoleId,
  }
}

export const setModuleAuthRoles = (
  moduleCode: string,
  authRoles: Omit<ModuleAuthRolesModel, 'moduleCode'> | ModuleAuthRolesModel
) => {
  const code = canonicalizeModuleCode(moduleCode)
  const key = code && getModuleAuthStorageKey(code)
  if (!code || !key) {
    return
  }

  const roles = authRoles.roles.map((role, index) => ({
    ...role,
    moduleCode: code,
    userRoleId:
      typeof role.userRoleId === 'number'
        ? role.userRoleId
        : LEGACY_MODULE_PREVIEW_ENABLED
        ? -(index + 1)
        : undefined,
  }))
  const requestedUserRoleId = authRoles.activeUserRoleId
  const stableRoleIndex = requestedUserRoleId
    ? roles.findIndex((role) => role.userRoleId === requestedUserRoleId)
    : -1
  const activeRoleIndex =
    stableRoleIndex >= 0
      ? stableRoleIndex
      : Math.max(0, Math.min(authRoles.activeRoleIndex || 0, Math.max(roles.length - 1, 0)))
  const activeRole = roles[activeRoleIndex]

  writeJson(
    getSessionStorage(),
    key,
    ({
      ...authRoles,
      moduleCode: code,
      roles,
      activeRoleIndex,
      activeUserRoleId: activeRole?.userRoleId,
      defaultRoute: authRoles.defaultRoute || activeRole?.defaultRoute,
    } as ModuleAuthRolesModel),
    'MODULE AUTH SESSION STORAGE'
  )
}

export const getAuthRoles = (): AuthRolesModel | ModuleAuthRolesModel | undefined => {
  const moduleCode = getRuntimeModuleCode()
  return moduleCode ? getModuleAuthRoles(moduleCode) : getLegacyAuthRoles()
}

export const setAuthRoles = (authRoles: AuthRolesModel) => {
  const responseModuleCode = authRoles.roles
    .map((role) => canonicalizeModuleCode(role.moduleCode))
    .find(Boolean)
  const moduleCode = responseModuleCode || getRuntimeModuleCode()

  if (moduleCode) {
    setModuleAuthRoles(moduleCode, {
      ...authRoles,
      activeUserRoleId: authRoles.roles[authRoles.activeRoleIndex]?.userRoleId,
      defaultRoute: authRoles.roles[authRoles.activeRoleIndex]?.defaultRoute,
    })
    return
  }

  setLegacyAuthRoles(authRoles)
}

export const setActiveRole = (index: number) => {
  const moduleCode = getRuntimeModuleCode()
  if (moduleCode) {
    const authRoles = getModuleAuthRoles(moduleCode)
    const selectedRole = authRoles?.roles[index]
    if (authRoles && selectedRole) {
      setModuleAuthRoles(moduleCode, {
        ...authRoles,
        activeRoleIndex: index,
        activeUserRoleId: selectedRole.userRoleId,
      })
    }
    return
  }

  const authRoles = getLegacyAuthRoles()
  if (authRoles?.roles[index]) {
    setLegacyAuthRoles({...authRoles, activeRoleIndex: index})
  }
}

export const setActiveRoleByUserRoleId = (userRoleId: number): boolean => {
  const moduleCode = getRuntimeModuleCode()
  if (!moduleCode) {
    return false
  }

  const authRoles = getModuleAuthRoles(moduleCode)
  const activeRoleIndex = authRoles?.roles.findIndex((role) => role.userRoleId === userRoleId) ?? -1
  if (!authRoles || activeRoleIndex < 0) {
    return false
  }

  setModuleAuthRoles(moduleCode, {...authRoles, activeRoleIndex, activeUserRoleId: userRoleId})
  return true
}

export const getActiveAuth = (): AuthModel | undefined => {
  const authRoles = getAuthRoles()
  if (!authRoles?.roles.length) {
    return undefined
  }

  if ('moduleCode' in authRoles && authRoles.activeUserRoleId) {
    const stableRole = authRoles.roles.find(
      (role) => role.userRoleId === authRoles.activeUserRoleId
    )
    if (stableRole) {
      return stableRole
    }
  }

  return authRoles.roles[authRoles.activeRoleIndex]
}

export const removeLegacyAuthRoles = () => {
  try {
    getLocalStorage()?.removeItem(AUTH_ROLES_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('AUTH ROLES LOCAL STORAGE REMOVE ERROR', error)
  }
}

export const removeModuleAuthRoles = (moduleCode: string) => {
  const key = getModuleAuthStorageKey(moduleCode)
  if (!key) {
    return
  }

  try {
    getSessionStorage()?.removeItem(key)
  } catch (error) {
    console.error('MODULE AUTH SESSION STORAGE REMOVE ERROR', error)
  }
}

export const removeAuthRoles = () => {
  const moduleCode = getRuntimeModuleCode()
  if (moduleCode) {
    removeModuleAuthRoles(moduleCode)
  } else {
    removeLegacyAuthRoles()
  }
}

export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = 'application/json'
  axios.interceptors.request.use(
    (config: {headers: {Authorization: string}}) => {
      const auth = getActiveAuth()
      if (auth?.token) {
        config.headers.Authorization = `Bearer ${auth.token}`
      }
      return config
    },
    (err: any) => Promise.reject(err)
  )

  axios.interceptors.response.use(
    (response: any) => response,
    async (error: any) => {
      const {response} = error
      if (response?.status === 403) {
        const rawMessage =
          typeof response.data === 'string'
            ? response.data
            : response.data?.message || response.data?.Message
        if (typeof rawMessage === 'string' && rawMessage.toLowerCase().includes('instantapi')) {
          window.alert(rawMessage)
        }
        return Promise.reject(error)
      }

      if (response?.status === 401) {
        const currentPath = window.location.pathname
        if (PUBLIC_ROUTES.some((route) => currentPath.startsWith(route))) {
          return Promise.reject(error)
        }

        const auth = getActiveAuth()
        if (auth?.refreshToken && !error.config?._moduleRefreshAttempted) {
          try {
            error.config._moduleRefreshAttempted = true
            const moduleCode = canonicalizeModuleCode(auth.moduleCode) || getRuntimeModuleCode()
            const {data: authResponse} = moduleCode
              ? await refreshModuleToken(auth.refreshToken)
              : await refreshToken(auth.refreshToken)
            const currentRoles = getAuthRoles()
            const activeRoleIndex = currentRoles
              ? Math.max(
                  0,
                  authResponse.findIndex(
                    (role) =>
                      Boolean(auth.userRoleId) && role.userRoleId === auth.userRoleId
                  ) >= 0
                    ? authResponse.findIndex((role) => role.userRoleId === auth.userRoleId)
                    : Math.min(currentRoles.activeRoleIndex, Math.max(authResponse.length - 1, 0))
                )
              : 0
            const activeRoleAuth = authResponse[activeRoleIndex]
            if (!activeRoleAuth) {
              throw new Error('Aktif rol tokeni yenilenemedi.')
            }

            setAuthRoles({roles: authResponse, activeRoleIndex})
            error.config.headers.Authorization = `Bearer ${activeRoleAuth.token}`
            return axios(error.config)
          } catch (refreshError) {
            removeAuthRoles()
            window.location.href = '/auth/login'
            return Promise.reject(refreshError)
          }
        }

        removeAuthRoles()
        window.location.href = '/auth/login'
      }

      return Promise.reject(error)
    }
  )
}

export {
  AUTH_LOCAL_STORAGE_KEY,
  AUTH_ROLES_LOCAL_STORAGE_KEY,
}
